import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProjectsAPI, BillingAPI } from "@/lib/api";
import { useAuth } from "@/lib/providers";
import { toast } from "sonner";
import LiveCritFeed from "@/components/LiveCritFeed";
import MyCreditsWidget from "@/components/MyCreditsWidget";
import BuyCreditsModal from "@/components/BuyCreditsModal";

export default function Studio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  // Free-tier paywall when a user tries to create a 2nd project.
  const [showPaywall, setShowPaywall] = useState(false);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data } = await ProjectsAPI.list(query);
      setProjects(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(""); }, [load]);

  // One-time welcome toast on first studio load for external (paying) users.
  // Keyed per-user in localStorage so a signed-in designer only sees it once —
  // not every time they come back to the dashboard. Internal users / admins
  // are skipped since the message is about the free-trial audit credit.
  useEffect(() => {
    if (!user?.user_id) return;
    if (user.user_type !== "external" || user.role === "admin") return;
    const key = `v_welcomed_${user.user_id}`;
    if (localStorage.getItem(key)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await BillingAPI.wallet();
        if (cancelled) return;
        const credits = Number(data?.wallet?.audit_credits || 0);
        const firstName = user.name?.split(" ")[0] || "designer";
        if (credits > 0) {
          toast.success(
            `Welcome, ${firstName} — you have ${credits} free audit${credits === 1 ? "" : "s"}, go run it on your pack.`,
            { duration: 8000 }
          );
        } else {
          toast.message(
            `Welcome, ${firstName} — your free credit is used up. Top up to run your next audit.`,
            { duration: 8000 }
          );
        }
      } catch (err) {
        // Welcome toast is cosmetic; a wallet fetch blip shouldn't noise
        // the console in prod or block the user. Still log in dev so a
        // broken toast is debuggable.
        if (process.env.NODE_ENV !== "production") {
          console.warn("Welcome-toast wallet fetch failed:", err?.message || err);
        }
      }
      finally {
        localStorage.setItem(key, new Date().toISOString());
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, user?.user_type, user?.role, user?.name]);

  const visibleProjects = useMemo(() => {
    return projects.filter((p) => (showArchived ? true : p.status !== "archived"));
  }, [projects, showArchived]);

  const totals = useMemo(() => {
    const live = projects.filter((p) => p.status === "live").length;
    const brands = new Set(projects.map((p) => p.brand_name)).size;
    return { live, brands, total: projects.length };
  }, [projects]);

  // Free-tier gate on "+ New project": external users with 0 paid credits
  // get exactly 1 project. For the first project they fly through; for a
  // 2nd they land on the paywall. Internal / admin / paying users bypass.
  const onNewProjectClick = async () => {
    const isExternal = user?.user_type === "external" && user?.role !== "admin";
    if (!isExternal) { navigate("/projects/new"); return; }
    try {
      const { data } = await BillingAPI.wallet();
      const lifetime = Number(data?.wallet?.lifetime_credits_purchased || 0);
      const isFreeTier = lifetime === 0;
      if (isFreeTier && projects.length >= 1) {
        setShowPaywall(true);
        return;
      }
    } catch (err) {
      // Backend 402 is the final arbiter on the submit path. We don't want
      // to block a user whose wallet fetch failed on a transient blip.
      if (process.env.NODE_ENV !== "production") {
        console.warn("Studio wallet pre-check failed:", err?.message || err);
      }
    }
    navigate("/projects/new");
  };

  return (
    <div className="v-container" style={{ paddingTop: 48, paddingBottom: 80 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div className="overline" data-testid="studio-overline">Studio / {user?.name?.split(" ")[0]}</div>
          <h1 className="display" style={{ fontSize: "clamp(40px,5vw,72px)", lineHeight: 1, marginTop: 12 }}>
            All your brands, <span className="v-gradient-text">all the verdicts.</span>
          </h1>
        </div>
        <button className="v-btn" data-testid="studio-new-project-btn" onClick={onNewProjectClick}>
          + New project
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8" style={{ marginTop: 48 }}>
        <div className="md:col-span-9">
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Live projects" value={totals.live} testId="stat-live" />
            <Stat label="Brands" value={totals.brands} testId="stat-brands" />
            <Stat label="Total projects" value={totals.total} testId="stat-total" />
          </div>

          <div style={{ marginTop: 40, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="v-input"
              placeholder="Search brand, client or category…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(q)}
              data-testid="studio-search-input"
              style={{ maxWidth: 360, flex: 1 }}
            />
            <button className="v-btn v-btn-ghost" data-testid="studio-search-btn" onClick={() => load(q)}>Search</button>
            {q && (
              <button className="v-btn v-btn-ghost" data-testid="studio-clear-btn" onClick={() => { setQ(""); load(""); }}>
                Clear
              </button>
            )}
            <button
              className="v-btn v-btn-ghost"
              data-testid="toggle-archived-btn"
              onClick={() => setShowArchived(!showArchived)}
              style={{ marginLeft: "auto" }}
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 v-stagger" style={{ marginTop: 24 }}>
            {loading && <div className="overline">Loading…</div>}
            {!loading && visibleProjects.length === 0 && (
              <div className="v-surface" style={{ padding: 32, gridColumn: "1 / -1" }} data-testid="studio-empty">
                <div className="overline">No projects yet</div>
                <p style={{ marginTop: 8, color: "var(--v-text-2)" }}>Create your first brand project to begin assessing packs.</p>
              </div>
            )}
            {visibleProjects.map((p) => (
              <Link key={p.project_id} to={`/projects/${p.project_id}`} data-testid={`project-card-${p.project_id}`} style={{ textDecoration: "none", color: "inherit" }}>
                <div className="v-surface" style={{ padding: 24, height: "100%", position: "relative", overflow: "hidden" }}>
                  {p.status === "archived" && (
                    <div className="mono" style={{ position: "absolute", top: 12, right: 12, fontSize: 10, color: "var(--v-text-3)", letterSpacing: ".18em", textTransform: "uppercase" }}>Archived</div>
                  )}
                  <div className="overline" style={{ color: p.project_type === "packaging_refresh" ? "var(--v-accent)" : "var(--v-text-2)" }}>
                    {p.project_type === "packaging_refresh" ? "Refresh" : "New development"} · {p.category}
                  </div>
                  <h3 style={{ fontSize: 26, marginTop: 10, letterSpacing: "-.02em" }}>{p.brand_name}</h3>
                  {p.client_name && <div className="mono" style={{ color: "var(--v-text-2)", marginTop: 4 }}>{p.client_name}</div>}
                  <p style={{ marginTop: 14, color: "var(--v-text-2)", fontSize: 14, lineHeight: 1.5, minHeight: 60 }}>{(p.positioning || "").slice(0, 160)}{(p.positioning || "").length > 160 ? "…" : ""}</p>
                  <div className="v-divider" style={{ marginTop: 16, paddingTop: 12, display: "flex", justifyContent: "space-between" }}>
                    <span className="mono" style={{ color: "var(--v-text-2)", fontSize: 11 }}>by {p.owner_name}</span>
                    <span className="overline" style={{ color: "var(--v-accent)" }}>Open →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <aside className="md:col-span-3">
          <div className="v-sticky-col">
            <MyCreditsWidget />
            <LiveCritFeed />
          </div>
        </aside>
      </div>
      <BuyCreditsModal open={showPaywall} onClose={() => setShowPaywall(false)} creditsAvailable={0} />
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="v-surface" style={{ padding: 22 }} data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="v-score" style={{ fontSize: 52, marginTop: 8 }}>{value}</div>
    </div>
  );
}
